import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ChevronLeft,
  ChevronRight,
  Sun,
  Moon,
  Download,
  Home,
  Users,
  Briefcase,
  Award,
  Globe,
  CheckCircle,
  ArrowRight,
  Building2,
  Zap,
  Shield,
  TrendingUp,
  Code,
  Database,
  Brain,
  Cpu,
  Mail,
  Phone,
  MapPin,
  Linkedin
} from 'lucide-react';

const SalesDeck = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isDark, setIsDark] = useState(false);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        nextSlide();
      } else if (e.key === 'ArrowLeft') {
        prevSlide();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const theme = {
    bg: isDark ? 'bg-slate-950' : 'bg-white',
    text: isDark ? 'text-white' : 'text-slate-900',
    textSecondary: isDark ? 'text-slate-400' : 'text-slate-600',
    accent: 'text-cyan-400',
    accentBg: isDark ? 'bg-cyan-500/10' : 'bg-cyan-50',
    cardBg: isDark ? 'bg-slate-900/50' : 'bg-slate-50',
    border: isDark ? 'border-slate-800' : 'border-slate-200',
  };

  const slides = [
    // Slide 1: Title
    {
      id: 'title',
      content: (
        <div className="flex flex-col items-center justify-center h-full text-center px-8">
          <Badge className={`mb-6 ${isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-100 text-cyan-700'} border-0`}>
            Company Overview 2026
          </Badge>
          <h1 className={`text-5xl md:text-7xl font-bold mb-6 ${theme.text}`}>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">Aurous</span>
            <span className={theme.text}> Consultancy</span>
          </h1>
          <p className={`text-xl md:text-2xl ${theme.textSecondary} max-w-3xl mb-8`}>
            AI-Powered IT Teams & Delivery Partner
          </p>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <Badge variant="outline" className={`${theme.border} ${theme.text} px-4 py-2`}>
              <Award className="w-4 h-4 mr-2 text-cyan-400" />
              Malaysia Digital Certified
            </Badge>
            <Badge variant="outline" className={`${theme.border} ${theme.text} px-4 py-2`}>
              <Globe className="w-4 h-4 mr-2 text-cyan-400" />
              4 Countries
            </Badge>
            <Badge variant="outline" className={`${theme.border} ${theme.text} px-4 py-2`}>
              <Building2 className="w-4 h-4 mr-2 text-cyan-400" />
              17+ Years
            </Badge>
          </div>
        </div>
      ),
    },
    // Slide 2: About Us
    {
      id: 'about',
      content: (
        <div className="flex flex-col h-full px-8 py-12">
          <Badge className={`mb-4 w-fit ${isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-100 text-cyan-700'} border-0`}>
            About Us
          </Badge>
          <h2 className={`text-4xl md:text-5xl font-bold mb-8 ${theme.text}`}>
            Who We Are
          </h2>
          <div className="grid md:grid-cols-2 gap-8 flex-1">
            <div className="space-y-6">
              <p className={`text-lg ${theme.textSecondary} leading-relaxed`}>
                Aurous Consultancy is a Malaysia Digital certified IT consultancy headquartered in Cyberjaya, 
                specializing in AI-powered engineering teams and technology delivery.
              </p>
              <p className={`text-lg ${theme.textSecondary} leading-relaxed`}>
                We help enterprises scale engineering capacity with pre-vetted AI talent, 
                managed delivery teams, and compliance-driven technology services.
              </p>
              <div className="grid grid-cols-2 gap-4 pt-4">
                {[
                  { label: 'Founded', value: '2008' },
                  { label: 'Employees', value: '500+' },
                  { label: 'Projects', value: '183+' },
                  { label: 'Clients', value: '100+' },
                ].map((stat, i) => (
                  <div key={i} className={`p-4 rounded-xl ${theme.cardBg}`}>
                    <div className={`text-3xl font-bold ${theme.accent}`}>{stat.value}</div>
                    <div className={theme.textSecondary}>{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className={`rounded-2xl ${theme.cardBg} p-6 flex flex-col justify-center`}>
              <h3 className={`text-xl font-semibold mb-4 ${theme.text}`}>Our Presence</h3>
              <div className="space-y-3">
                {[
                  { country: 'Malaysia', city: 'Cyberjaya (HQ)', flag: '🇲🇾' },
                  { country: 'Singapore', city: 'Singapore', flag: '🇸🇬' },
                  { country: 'India', city: 'Bangalore & Chennai', flag: '🇮🇳' },
                  { country: 'Hong Kong', city: 'Hong Kong', flag: '🇭🇰' },
                ].map((loc, i) => (
                  <div key={i} className={`flex items-center gap-3 p-3 rounded-lg ${isDark ? 'bg-slate-800/50' : 'bg-white'}`}>
                    <span className="text-2xl">{loc.flag}</span>
                    <div>
                      <div className={`font-medium ${theme.text}`}>{loc.country}</div>
                      <div className={`text-sm ${theme.textSecondary}`}>{loc.city}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ),
    },
    // Slide 3: Services
    {
      id: 'services',
      content: (
        <div className="flex flex-col h-full px-8 py-12">
          <Badge className={`mb-4 w-fit ${isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-100 text-cyan-700'} border-0`}>
            Our Services
          </Badge>
          <h2 className={`text-4xl md:text-5xl font-bold mb-8 ${theme.text}`}>
            What We Offer
          </h2>
          <div className="grid md:grid-cols-3 gap-6 flex-1">
            {[
              {
                icon: Users,
                title: 'AI-Powered Contract Engineers',
                desc: 'Pre-vetted engineers with AI skills ready to accelerate your projects',
                features: ['AI Integration', 'Full-Stack Dev', 'Data Engineering']
              },
              {
                icon: Briefcase,
                title: 'Managed Delivery Teams',
                desc: 'Complete project pods with built-in AI practices and tools',
                features: ['End-to-End Delivery', 'Agile Teams', 'Fixed Outcomes']
              },
              {
                icon: Brain,
                title: 'AI & Data Engineering',
                desc: 'Custom AI solutions, ML models, and data pipeline development',
                features: ['LLM Integration', 'ML Models', 'Data Pipelines']
              },
              {
                icon: Shield,
                title: 'AI Governance & Compliance',
                desc: 'Responsible AI implementation with regulatory compliance',
                features: ['AI Audits', 'Risk Assessment', 'Policy Framework']
              },
              {
                icon: Code,
                title: 'QA & AI Testing',
                desc: 'AI-augmented testing services for quality assurance',
                features: ['Test Automation', 'AI Testing', 'Performance QA']
              },
              {
                icon: Database,
                title: 'Enterprise Solutions',
                desc: 'Core banking, insurance, and fintech implementations',
                features: ['Banking Systems', 'Insurance Tech', 'Fintech']
              },
            ].map((service, i) => (
              <div key={i} className={`p-6 rounded-2xl ${theme.cardBg} border ${theme.border} hover:border-cyan-500/50 transition-all group`}>
                <div className={`w-12 h-12 rounded-xl ${isDark ? 'bg-cyan-500/20' : 'bg-cyan-100'} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <service.icon className="w-6 h-6 text-cyan-500" />
                </div>
                <h3 className={`text-lg font-semibold mb-2 ${theme.text}`}>{service.title}</h3>
                <p className={`text-sm ${theme.textSecondary} mb-4`}>{service.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {service.features.map((f, j) => (
                    <span key={j} className={`text-xs px-2 py-1 rounded-full ${isDark ? 'bg-slate-800 text-slate-300' : 'bg-slate-200 text-slate-700'}`}>
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    // Slide 4: Industries
    {
      id: 'industries',
      content: (
        <div className="flex flex-col h-full px-8 py-12">
          <Badge className={`mb-4 w-fit ${isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-100 text-cyan-700'} border-0`}>
            Industries We Serve
          </Badge>
          <h2 className={`text-4xl md:text-5xl font-bold mb-8 ${theme.text}`}>
            Domain Expertise
          </h2>
          <div className="grid md:grid-cols-4 gap-4 flex-1">
            {[
              { name: 'Banking & Finance', icon: '🏦', projects: '45+' },
              { name: 'Insurance & Takaful', icon: '🛡️', projects: '38+' },
              { name: 'Telecommunications', icon: '📡', projects: '22+' },
              { name: 'Government & Public', icon: '🏛️', projects: '18+' },
              { name: 'Healthcare', icon: '🏥', projects: '15+' },
              { name: 'Retail & E-commerce', icon: '🛒', projects: '12+' },
              { name: 'Manufacturing', icon: '🏭', projects: '10+' },
              { name: 'Energy & Utilities', icon: '⚡', projects: '8+' },
            ].map((industry, i) => (
              <div key={i} className={`p-6 rounded-2xl ${theme.cardBg} border ${theme.border} text-center hover:border-cyan-500/50 transition-all`}>
                <div className="text-4xl mb-3">{industry.icon}</div>
                <h3 className={`font-semibold mb-2 ${theme.text}`}>{industry.name}</h3>
                <div className={`text-2xl font-bold ${theme.accent}`}>{industry.projects}</div>
                <div className={`text-sm ${theme.textSecondary}`}>Projects</div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    // Slide 5: Key Clients
    {
      id: 'clients',
      content: (
        <div className="flex flex-col h-full px-8 py-12">
          <Badge className={`mb-4 w-fit ${isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-100 text-cyan-700'} border-0`}>
            Our Clients
          </Badge>
          <h2 className={`text-4xl md:text-5xl font-bold mb-8 ${theme.text}`}>
            Trusted by Industry Leaders
          </h2>
          <div className={`flex-1 rounded-2xl ${theme.cardBg} p-8`}>
            <div className="grid grid-cols-4 md:grid-cols-6 gap-6">
              {[
                'Maybank', 'CIMB', 'Public Bank', 'RHB Bank',
                'Hong Leong', 'AmBank', 'AIA', 'Prudential',
                'Great Eastern', 'Sun Life', 'Manulife', 'Allianz',
                'Celcom', 'Maxis', 'Digi', 'TM',
                'Petronas', 'TNB', 'Bursa Malaysia', 'EPF',
                'KWAP', 'LHDN', 'MDEC', 'Bank Negara'
              ].map((client, i) => (
                <div key={i} className={`p-4 rounded-xl ${isDark ? 'bg-slate-800/50' : 'bg-white'} flex items-center justify-center border ${theme.border}`}>
                  <span className={`font-medium text-sm ${theme.text} text-center`}>{client}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-center gap-8 mt-8">
            <div className="text-center">
              <div className={`text-4xl font-bold ${theme.accent}`}>100+</div>
              <div className={theme.textSecondary}>Enterprise Clients</div>
            </div>
            <div className="text-center">
              <div className={`text-4xl font-bold ${theme.accent}`}>16</div>
              <div className={theme.textSecondary}>Industries Served</div>
            </div>
            <div className="text-center">
              <div className={`text-4xl font-bold ${theme.accent}`}>95%</div>
              <div className={theme.textSecondary}>Client Retention</div>
            </div>
          </div>
        </div>
      ),
    },
    // Slide 6: Why Choose Us
    {
      id: 'why-us',
      content: (
        <div className="flex flex-col h-full px-8 py-12">
          <Badge className={`mb-4 w-fit ${isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-100 text-cyan-700'} border-0`}>
            Why Aurous
          </Badge>
          <h2 className={`text-4xl md:text-5xl font-bold mb-8 ${theme.text}`}>
            Our Competitive Edge
          </h2>
          <div className="grid md:grid-cols-2 gap-8 flex-1">
            {[
              {
                icon: Zap,
                title: 'AI-First Approach',
                desc: 'Every engineer is trained on AI tools. Every project leverages AI for productivity gains of 30-50%.'
              },
              {
                icon: Shield,
                title: 'Compliance Ready',
                desc: 'BNM, MAS, and industry-specific compliance built into our delivery DNA. Perfect for regulated industries.'
              },
              {
                icon: TrendingUp,
                title: 'Proven Track Record',
                desc: '183+ successful projects across banking, insurance, telco, and government sectors.'
              },
              {
                icon: Users,
                title: 'Talent Depth',
                desc: '500+ pre-vetted engineers available on-demand. Scale up or down based on your needs.'
              },
              {
                icon: Globe,
                title: 'Regional Presence',
                desc: 'Offices in Malaysia, Singapore, India, and Hong Kong for follow-the-sun delivery.'
              },
              {
                icon: Award,
                title: 'Certifications',
                desc: 'Malaysia Digital certified, MOF registered, ISO compliant. Trusted by government and enterprises.'
              },
            ].map((item, i) => (
              <div key={i} className={`flex gap-4 p-6 rounded-2xl ${theme.cardBg} border ${theme.border}`}>
                <div className={`w-12 h-12 rounded-xl ${isDark ? 'bg-cyan-500/20' : 'bg-cyan-100'} flex items-center justify-center flex-shrink-0`}>
                  <item.icon className="w-6 h-6 text-cyan-500" />
                </div>
                <div>
                  <h3 className={`text-lg font-semibold mb-2 ${theme.text}`}>{item.title}</h3>
                  <p className={`${theme.textSecondary}`}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    // Slide 7: Products
    {
      id: 'products',
      content: (
        <div className="flex flex-col h-full px-8 py-12">
          <Badge className={`mb-4 w-fit ${isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-100 text-cyan-700'} border-0`}>
            AurousOrbit.ai
          </Badge>
          <h2 className={`text-4xl md:text-5xl font-bold mb-8 ${theme.text}`}>
            Our AI Product Suite
          </h2>
          <div className="grid md:grid-cols-3 gap-6 flex-1">
            {[
              {
                name: 'MyHR Orbit',
                desc: 'AI-powered HR management with automated workflows, smart recruitment, and employee analytics.',
                color: 'from-blue-500 to-blue-600',
                features: ['AI Recruitment', 'Performance Analytics', 'Payroll Automation']
              },
              {
                name: 'MyFinance Orbit',
                desc: 'Intelligent financial management with AI-driven forecasting, expense tracking, and compliance.',
                color: 'from-green-500 to-green-600',
                features: ['AI Forecasting', 'Invoice Automation', 'Compliance Reporting']
              },
              {
                name: 'MyProcurement Orbit',
                desc: 'Smart procurement system with AI vendor selection, automated RFQs, and spend analytics.',
                color: 'from-orange-500 to-orange-600',
                features: ['Vendor Management', 'Automated RFQ', 'Spend Analytics']
              },
              {
                name: 'MyGovernance Orbit',
                desc: 'AI-powered governance with risk assessment, compliance monitoring, and policy management.',
                color: 'from-purple-500 to-purple-600',
                features: ['Risk Assessment', 'Compliance Monitoring', 'Policy Management']
              },
              {
                name: 'MySales Orbit',
                desc: 'Smart CRM with AI lead scoring, predictive analytics, and automated follow-ups.',
                color: 'from-pink-500 to-pink-600',
                features: ['Lead Scoring', 'Sales Predictions', 'Pipeline Automation']
              },
              {
                name: 'MyDoc Orbit',
                desc: 'AI document management with smart search, auto-categorization, and compliance tracking.',
                color: 'from-cyan-500 to-cyan-600',
                features: ['Smart Search', 'Auto-Tagging', 'Version Control']
              },
            ].map((product, i) => (
              <div key={i} className={`p-5 rounded-2xl ${theme.cardBg} border ${theme.border} overflow-hidden relative`}>
                <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${product.color} opacity-10 rounded-full -translate-y-1/2 translate-x-1/2`} />
                <h3 className={`text-xl font-bold mb-2 ${theme.text}`}>{product.name}</h3>
                <p className={`${theme.textSecondary} text-sm mb-3`}>{product.desc}</p>
                <div className="flex flex-wrap gap-1">
                  {product.features.map((f, j) => (
                    <span key={j} className={`text-xs px-2 py-1 rounded-full bg-gradient-to-r ${product.color} text-white`}>
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    // Slide 8: Contact
    {
      id: 'contact',
      content: (
        <div className="flex flex-col items-center justify-center h-full text-center px-8">
          <Badge className={`mb-6 ${isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-100 text-cyan-700'} border-0`}>
            Let's Connect
          </Badge>
          <h2 className={`text-4xl md:text-6xl font-bold mb-6 ${theme.text}`}>
            Ready to Transform Your
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500"> IT Delivery?</span>
          </h2>
          <p className={`text-xl ${theme.textSecondary} max-w-2xl mb-10`}>
            Let's discuss how Aurous can help you build AI-powered teams and accelerate your technology initiatives.
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-10 w-full max-w-3xl">
            <div className={`p-6 rounded-2xl ${theme.cardBg} border ${theme.border}`}>
              <Mail className="w-8 h-8 text-cyan-500 mx-auto mb-3" />
              <div className={`font-medium ${theme.text}`}>Email</div>
              <div className={theme.textSecondary}>enquiry@aurousconsultancy.com</div>
            </div>
            <div className={`p-6 rounded-2xl ${theme.cardBg} border ${theme.border}`}>
              <Phone className="w-8 h-8 text-cyan-500 mx-auto mb-3" />
              <div className={`font-medium ${theme.text}`}>Phone</div>
              <div className={theme.textSecondary}>+60 3-8322 0562</div>
            </div>
            <div className={`p-6 rounded-2xl ${theme.cardBg} border ${theme.border}`}>
              <MapPin className="w-8 h-8 text-cyan-500 mx-auto mb-3" />
              <div className={`font-medium ${theme.text}`}>Location</div>
              <div className={theme.textSecondary}>Cyberjaya, Malaysia</div>
            </div>
          </div>
          <div className="flex gap-4">
            <Link to="/#contact">
              <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white px-8">
                Get in Touch
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/">
              <Button size="lg" variant="outline" className={`${theme.border} ${theme.text}`}>
                Visit Website
              </Button>
            </Link>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className={`min-h-screen ${theme.bg} transition-colors duration-300`}>
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 px-6 py-4 flex items-center justify-between ${isDark ? 'bg-slate-950/80' : 'bg-white/80'} backdrop-blur-xl border-b ${theme.border}`}>
        <Link to="/" className="flex items-center gap-2">
          <Home className={`w-5 h-5 ${theme.textSecondary}`} />
          <span className={theme.textSecondary}>Back to Website</span>
        </Link>
        
        <div className="flex items-center gap-2">
          <span className={`text-sm ${theme.textSecondary}`}>
            {currentSlide + 1} / {slides.length}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsDark(!isDark)}
            className={theme.text}
          >
            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-16 min-h-screen">
        <div className="h-[calc(100vh-4rem)] overflow-hidden">
          {slides[currentSlide].content}
        </div>
      </main>

      {/* Navigation */}
      <footer className={`fixed bottom-0 left-0 right-0 px-6 py-4 flex items-center justify-between ${isDark ? 'bg-slate-950/80' : 'bg-white/80'} backdrop-blur-xl border-t ${theme.border}`}>
        <Button
          variant="ghost"
          onClick={prevSlide}
          disabled={currentSlide === 0}
          className={theme.text}
        >
          <ChevronLeft className="w-5 h-5 mr-2" />
          Previous
        </Button>

        {/* Slide indicators */}
        <div className="flex gap-2">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              className={`w-2 h-2 rounded-full transition-all ${
                i === currentSlide
                  ? 'w-8 bg-cyan-500'
                  : isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-slate-300 hover:bg-slate-400'
              }`}
            />
          ))}
        </div>

        <Button
          variant="ghost"
          onClick={nextSlide}
          disabled={currentSlide === slides.length - 1}
          className={theme.text}
        >
          Next
          <ChevronRight className="w-5 h-5 ml-2" />
        </Button>
      </footer>
    </div>
  );
};

export default SalesDeck;
